import {logger} from '../../../srcCommon/services/logger/logger';
import {AppStore} from '../../store/appStore';
import {getHasPerformanceDebugLogging} from '../../store/featureFlags/featureFlagsSelectors';
import {getElectronMemoryAsync} from '../electron/electronBridgeOutbound';

/*
 * Setup.
 */

const initialMemoryLogDelay = 30 * 1000;
const memoryLogPeriod = 5 * 60 * 1000;

const memoryLogger = logger.getLogger('memory');
const sessionStartTime = performance.now();

/*
 * API.
 */

export function setupMemoryLogging(store: AppStore) {
  if (!getHasPerformanceDebugLogging(store.getState())) {
    return;
  }

  if (!performance.memory) {
    return;
  }

  setTimeout(logMemoryAsync, initialMemoryLogDelay);
}

declare global {
  interface MemoryInfo {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
  }

  interface Performance {
    memory?: MemoryInfo;
  }
}

declare namespace Electron {
  interface HeapStatistics {
    totalHeapSize: number;
    totalHeapSizeExecutable: number;
    totalPhysicalSize: number;
    totalAvailableSize: number;
    usedHeapSize: number;
    heapSizeLimit: number;
    mallocedMemory: number;
    peakMallocedMemory: number;
    doesZapGarbage: boolean;
  }

  interface BlinkMemoryInfo {
    allocated: number;
    marked?: number;
    total: number;
  }

  interface ProcessMemoryInfo {
    private: number;
    residentSet?: number;
    shared: number;
  }

  interface MemoryUsageDetails {
    count: number;
    liveSize: number;
    size: number;
  }

  interface ResourceUsage {
    images: MemoryUsageDetails;
    scripts: MemoryUsageDetails;
    cssStyleSheets: MemoryUsageDetails;
    xslStyleSheets: MemoryUsageDetails;
    fonts: MemoryUsageDetails;
    other: MemoryUsageDetails;
  }
}

interface ElectronMemory {
  heapStatistics: Electron.HeapStatistics;
  blinkMemoryInfo: Electron.BlinkMemoryInfo;
  processMemoryInfo: Electron.ProcessMemoryInfo;
  resourceUsage: Electron.ResourceUsage;
}

function formatMemoryUsageDetails(resourceUsage: Electron.MemoryUsageDetails) {
  return {
    count: resourceUsage.count,
    live_size: resourceUsage.liveSize,
    size: resourceUsage.size,
  };
}

async function logMemoryAsync() {
  const performanceMemory = performance.memory;
  const electronMemory: ElectronMemory | undefined = await getElectronMemoryAsync();

  memoryLogger.info('Memory report', {
    session_duration: performance.now() - sessionStartTime,
    ...(performanceMemory && {
      performance_memory: {
        js_heap_size_limit: performanceMemory.jsHeapSizeLimit,
        total_js_heap_size: performanceMemory.totalJSHeapSize,
        used_js_heap_size: performanceMemory.usedJSHeapSize,
      },
    }),
    ...(electronMemory && {
      electron_memory: {
        heap_statistics: {
          total_heap_size: electronMemory.heapStatistics.totalHeapSize,
          total_heap_size_executable: electronMemory.heapStatistics.totalHeapSizeExecutable,
          total_physical_size: electronMemory.heapStatistics.totalPhysicalSize,
          total_available_size: electronMemory.heapStatistics.totalAvailableSize,
          used_heap_size: electronMemory.heapStatistics.usedHeapSize,
          heap_size_limit: electronMemory.heapStatistics.heapSizeLimit,
          malloced_memory: electronMemory.heapStatistics.mallocedMemory,
          peak_malloced_memory: electronMemory.heapStatistics.peakMallocedMemory,
          does_zap_garbage: electronMemory.heapStatistics.doesZapGarbage,
        },
        blink_memory_info: {
          allocated: electronMemory.blinkMemoryInfo.allocated,
          ...(electronMemory.blinkMemoryInfo.marked && {marked: electronMemory.blinkMemoryInfo.marked}),
          total: electronMemory.blinkMemoryInfo.total,
        },
        process_memory_info: {
          private: electronMemory.processMemoryInfo.private,
          ...(electronMemory.processMemoryInfo.residentSet && {
            resident_set: electronMemory.processMemoryInfo.residentSet,
          }),
          shared: electronMemory.processMemoryInfo.shared,
        },
        resource_usage: {
          images: formatMemoryUsageDetails(electronMemory.resourceUsage.images),
          scripts: formatMemoryUsageDetails(electronMemory.resourceUsage.scripts),
          css_style_sheets: formatMemoryUsageDetails(electronMemory.resourceUsage.cssStyleSheets),
          xsl_style_sheets: formatMemoryUsageDetails(electronMemory.resourceUsage.xslStyleSheets),
          fonts: formatMemoryUsageDetails(electronMemory.resourceUsage.fonts),
          other: formatMemoryUsageDetails(electronMemory.resourceUsage.other),
        },
      },
    }),
  });

  setTimeout(logMemoryAsync, memoryLogPeriod);
}
